import SlDrawer from "@shoelace-style/shoelace/dist/components/drawer/drawer.js";
import "@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/visually-hidden/visually-hidden.js";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/drawer/drawer.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/switch/switch.js";

import { html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { MessageType, ToastMessage, ToastMessageEvent } from "../../EventSystem";
import { choose } from "lit/directives/choose.js";
import { until } from "lit/directives/until.js";
import { litIf } from "../LitUtils";

import "../auth/Signin";
import "../map/PlacesMap";
import "../Compass/Compass";
import "../FilterEditor/FilterEditor";
import "../DataTable";
import "../Journal/JournalList";
import "../GPXExport";
import "../Media/MediaImport";
import "../CopyToClipboard";
import "../SettingsControl/SettingsControl";
import "../GoToLocationControl";
import "../PlaceToolbar";
import "../AddPlace/AddPlace";
import "../AddPlace/AddPlaceDialog";
import "../PlaceView/PlaceViewDialog";
import "../FullscreenWindows";
import "../SearchBar";
import "../Markdown";
import "../FilterBar";
import "../Navigator/Navigator";
import "../ContributorStats/ContributorStatsDialog";
import "../Moderation/ModerationList";
import "../BulkImport/BulkImport";
import "../Profile/ProfileDialog";
import "../cache/MapCacheControl";
import "../MediaUploadBrowser/MediaUploadBrowser";
import "../UserProfile/UserProfileDialog";
import "../ToolDrawer";

import { IPlace, IPlaceMediaItem, makeEmptyPlace } from "../../models/Models";
import { ILocation, locationToFixed } from "../../models/ILocation";
import { PlacesMap, MAX_ZOOM, MapPOI, MarkerType } from "../map/PlacesMap";
import { openInMapsApp, forceReloadPage } from "../../Utils";
import { BaseElement } from "../BaseElement";
import { IModification } from "../../backend/cloud/JournalTable";
import { MediaImport } from "../Media/MediaImport";
import { PlaceViewDialog } from "../PlaceView/PlaceViewDialog";
import { Router } from "../Router";

import styles from "./MainWindow.styles";
import { getModuleLog } from "../../Logger";

import cloneDeep from "lodash-es/cloneDeep.js";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { JobProgressEvent } from "../../PersistentJobQueue";
import { shareContent } from "../../Utils";
import { JobType } from "../../JobQueueInterface";
import { LightTheme } from "../ThemeLight";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { DataClient } from "../../DataStore/LocalDataStore";
import { showFooterMessage } from "../../Kernel";
import { userCanEditPlaceMedia } from "../../backend/IPlaceMediaTable";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button.js";
import { LocalStorageProperty } from "../../LocalStorageProperty";
import { showConfirmDialog } from "../ConfirmDialog";
const DEFAULT_TOAST_DURATION = 3000;

type DrawerType = "journal" | "settings" | "filter" | "import" | "moderation" | "uploads";

@customElement("ds-cave-db-main-window")
export class MainWindow extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: String }) appTitle?: string;

    @state() protected _newVersionAvailable = false;
    @state() protected _newVersionUrl = "";
    @state() protected _selectedPlace?: IPlace;
    @state() protected _currentDeletePlaceId?: string;
    @state() protected _navigating = false;
    @state() protected _view: "map" | "data-table" = "map";
    @state() protected _trackLocation = false;
    @state() protected _infoMessage?: string;
    @state() protected _currentDrawer?: DrawerType;
    @state() protected _showGoToLocationDialog = false;
    @state() protected _mapCenter?: ILocation;
    @state() protected _currentSelectedLocation?: ILocation;
    @state() protected _mediaGalleryOpen = false;
    @state() protected _mediaGalleryFilter?: string[];
    @state() protected _mediaGalleryIndex?: number;
    @state() protected _mediaGalleryMedia?: IPlaceMediaItem[];
    @state() protected _showCompass = this._kernel.showCompass.get();
    @state() protected _currentImportMediaTarget?: IPlace;
    @state() protected _signedIn = false;
    @state() protected _syncing = true;
    @state() protected _showResetFilterButton = true;

    @state() protected _selectedUserProfileId?: string;

    // Media importing
    @state() protected _importingMedia = false;
    @state() protected _mapPOIS: MapPOI[] = [];

    @state() protected _mapMarkerType: MarkerType = "details";

    private _mapMarkerStored = new LocalStorageProperty(
        this._kernel.events,
        "map-marker-type",
        1,
        "map-marker-type",
        "details"
    );

    private _navigateToSiteId?: string;
    private _navigateToUserId?: string;
    private _selectedUserId?: string;

    private _currentInfoMessageTimerId: NodeJS.Timeout | undefined;
    private _importSelectingPlace = false;
    private _movingPlace = false;
    private _router!: Router;

    private _placesTable = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _userTable = this._kernel.userSettings;

    private _lastToolDrawerTool = new LocalStorageProperty(
        this._kernel.events,
        "last-tool-drawer-tool",
        1,
        "last-tool-drawer-tool",
        "filter"
    );

    private _log = getModuleLog("main-app");
    constructor() {
        super();
    }

    private _setMarkerType(type: MarkerType) {
        this._mapMarkerType = type;
        this._mapMarkerStored.set(type);
    }

    async _tryOpenPlace() {
        const id = this._navigateToSiteId;
        if (id) {
            this._log.debug(`tryOpenPlace: ${id}`);
            const place = await this._placesTable.read(id);
            if (place) {
                this._selectPlace(id);
                this._navigateToSiteId = undefined;
                this._log.debug(`tryOpenPlace: succeeded`);
            }
        }
    }

    async _tryOpenUserProfile() {
        const id = this._navigateToUserId;
        if (id) {
            this._log.debug(`tryOpenUser: ${id}`);
            const user = await this._userTable.read(id);
            if (user) {
                this._selectedUserId = this._navigateToUserId;
                this._navigateToUserId = undefined;
                this._openDialog("user-profile", { home: false });
                this._log.debug(`tryOpenUser: succeeded`);
            }
        }
    }

    async onChangeSet(provider: DataProvider, _changeSet: ChangeSet<any>): Promise<void> {
        if (provider.name === "places") {
            this._tryOpenPlace();
        } else if (provider.name === "users") {
            this._tryOpenUserProfile();
        }
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._log.debug("Initialising UI");

        this._mapMarkerType = this._mapMarkerStored.get() as MarkerType;

        this._kernel.events.subscribe("current-place-filter-changed", () => {
            this._updateShowResetFilterButton();
            this.requestUpdate();
        });
        this._subscribeToKernelEvent(
            "show-compass-changed",
            (show: boolean) => (this._showCompass = show)
        );
        this._subscribeToKernelEvent("ds-journal-entry-selected", async (entry: IModification) => {
            this._router?.navigate(`/place/${entry.modelId}`);
        });
        this._subscribeToKernelEvent("ds-place-selected", async (id: string) => {
            this._router?.navigate(`/place/${id}`);
        });
        this._subscribeToKernelEvent("ds-user-selected", async (id: string) => {
            this._router?.navigate(`/user/${id}`);
        });
        this._subscribeToKernelEvent(
            this._kernel.mediaLibrary.JOB_PROGRESS_EVENT,
            (e: JobProgressEvent) => {
                this._showInfoMessage({
                    msg: `Uploading ${e.index + 1}/${e.count}`,
                    duration: -1,
                    type: MessageType.Info,
                });
            }
        );

        this._subscribeToKernelEvent(
            this._kernel.tileDownloader.JOB_PROGRESS_EVENT,
            (e: JobProgressEvent) => {
                this._showInfoMessage({
                    msg: `${e.type === JobType.Delete ? "Deleting cache" : "Caching"} ${e.index}/${
                        e.count
                    }`,
                    duration: -1,
                    type: MessageType.Info,
                });
            }
        );

        this._subscribeToKernelEvent(
            this._kernel.placeMediaDownloader.JOB_PROGRESS_EVENT,
            (e: JobProgressEvent) => {
                this._showInfoMessage({
                    msg: `Caching media ${e.index + 1}/${e.count}`,
                    duration: -1,
                    type: MessageType.Info,
                });
            }
        );

        this._subscribeToKernelEvent(ToastMessage, (event: ToastMessageEvent) => {
            this._showInfoMessage(event);
        });

        this._subscribeToKernelEvent(
            "map-center-changed",
            (e: CustomEvent) => (this._mapCenter = e.detail.center)
        );

        this.addEventListener("ds-view-on-map", (e: CustomEvent) => {
            this._mapGoTo(e.detail.location, MAX_ZOOM);

            if (e.detail.placeId) {
                this._kernel.events.raiseEvent("ds-place-selected", e.detail.placeId);
            }
        });

        this.addEventListener("ds-open-in-map-app", (event: Event) => {
            const ce = event as CustomEvent;
            openInMapsApp(ce.detail.place);
        });

        this.addEventListener("ds-open-gallery", (e: CustomEvent) => {
            this._mediaGalleryOpen = true;
            this._mediaGalleryFilter = e.detail?.filter;
            this._mediaGalleryIndex = e.detail?.index;
            this._mediaGalleryMedia = e.detail?.media;
        });

        this.addEventListener("ds-input-coords", () => {
            this._closeDrawer();
            this._openDialog("go-to-location");
        });
        this.addEventListener("ds-track-location", (e: Event) =>
            this._kernel.trackLocation((e as CustomEvent).detail.enableTracking)
        );
        this.addEventListener("ds-stop-navigation", () => this._kernel.stopNavigating());
        this.addEventListener("ds-bulk-import", () => this._openDialog("bulk-import"));
        this.addEventListener("ds-share-place", (event_: Event) => {
            const event = event_ as CustomEvent;
            const place = event.detail.place;

            if (place) {
                shareContent(
                    `${place.data.name} : ${place.data.spelioData.type}`,
                    `${place.data.name} : ${place.data.spelioData.type}`,
                    `${this._kernel.siteRootUrl}/share/${place.id}.html`
                );
            }
        });

        this.addEventListener("ds-open-user-profile", (e: Event) =>
            this._router?.navigate(`/user/${(e as CustomEvent).detail.id}`)
        );

        this.addEventListener("ds-delete-user", () => this._openDialog("delete-user"));

        this._subscribeToKernelEvent("ds-navigate", (args: any) => {
            const location = args.location;
            if (location) {
                this._kernel.startNavigating(location);
            }
            this._router?.navigate("/", { silent: true });
        });

        this._subscribeToKernelEvent(
            "ds-location-tracking-started",
            () => (this._trackLocation = true)
        );
        this._subscribeToKernelEvent(
            "ds-location-tracking-stopped",
            () => (this._trackLocation = false)
        );

        this._subscribeToKernelEvent("ds-navigation-started", () => {
            this._onNavigationStarted();
        });
        this._subscribeToKernelEvent("ds-navigation-stopped", () => {
            this._onNavigationStopped();
        });

        this._subscribeToKernelEvent("ds-new-version-available", () => {
            this._newVersionAvailable = true;
            this._newVersionUrl = "./release-notes.md";
            this._openDialog("new-version");
        });

        this._subscribeToKernelEvent("ds-navigate-view-place", (id: string) => {
            this._navigateToSiteId = id;
            this._tryOpenPlace();
        });

        this._subscribeToKernelEvent("ds-navigate-view-user", (id: string) => {
            this._navigateToUserId = id;
            this._tryOpenUserProfile();
        });

        this._subscribeToKernelEvent("ds-navigate-home", () => {
            if (!this._movingPlace) {
                this._currentDialog = undefined;
                this._currentDrawer = undefined;
            }
        });

        this._subscribeToKernelEvent("ds-navigate-changelog", () => {
            this._currentDialog = undefined;
            this._currentDrawer = "journal";
        });

        this._subscribeToKernelEvent("ds-navigate-moderation", () => {
            this._currentDialog = undefined;
            this._currentDrawer = "moderation";
        });

        this._subscribeToKernelEvent("ds-navigate-settings", () => {
            this._currentDialog = undefined;
            this._currentDrawer = "settings";
        });

        this._subscribeToKernelEvent("ds-navigate-uploads", () => {
            this._currentDialog = undefined;
            this._currentDrawer = "uploads";
        });
        this._subscribeToKernelEvent("datastore-ready", () => {
            this._syncing = false;
            if (this._navigateToSiteId) {
                showFooterMessage(
                    `Place ${this._navigateToSiteId} does not exist`,
                    MessageType.Error
                );
                this._navigateToSiteId = undefined;
                this._setHome();
            }
            if (this._navigateToUserId) {
                showFooterMessage(
                    `User ${this._navigateToUserId} does not exist`,
                    MessageType.Error
                );
                this._navigateToUserId = undefined;
                this._setHome();
            }
        });

        this._signedIn = (await this._kernel.getUser()) !== undefined;
        this._subscribeToKernelEvent("auth-user-signed-in", () => {
            this._signedIn = true;
        });
        this._subscribeToKernelEvent("auth-user-signed-out", () => {
            this._signedIn = false;
        });

        this._kernel.dataStore.connectClient(
            new DataClient(this, [
                { source: "places", paths: [] },
                { source: "users", paths: [] },
            ])
        );
        this._updateShowResetFilterButton();
        this._router = new Router(this._kernel.events);
        this._log.debug("Initialising UI complete");
    }

    private _updateShowResetFilterButton() {
        this._showResetFilterButton = true;
    }

    override firstUpdated() {
        addEventListener("load", () => {
            document.body.classList.remove("unresolved");
        });
    }

    private _showInfoMessage(info: ToastMessageEvent) {
        if (this._currentInfoMessageTimerId) {
            clearTimeout(this._currentInfoMessageTimerId);
            this._currentInfoMessageTimerId = undefined;
        }
        this._infoMessage = info.msg;
        setTimeout(() => {
            if (info.duration !== 0) {
                this._currentInfoMessageTimerId = setTimeout(
                    () => {
                        this._infoMessage = undefined;
                        this._currentInfoMessageTimerId = undefined;
                    },
                    info.duration === -1 ? DEFAULT_TOAST_DURATION : info.duration
                );
            }
        });
    }

    private _openDrawer(type: DrawerType | undefined) {
        this._currentDrawer = type;
    }

    private _openSettingsDrawer() {
        this._router?.navigate("/settings");
    }

    private _openJournalDrawer() {
        this._router?.navigate("/changelog");
    }

    private _openModerationDrawer() {
        this._router?.navigate("/moderation");
    }

    private async _openFilterDrawer() {
        this._openDrawer("filter");
    }

    private _openImportDrawer() {
        this._openDrawer("import");
    }

    protected override _closeDrawer() {
        if (this._currentDrawer) {
            this._setHome();
        }
        this._currentDrawer = undefined;
    }

    private _getPlaceViewer() {
        return this.shadowRoot?.getElementById("place-view") as PlaceViewDialog;
    }

    private _getImportDrawer() {
        return this.shadowRoot?.getElementById("drawer-import") as SlDrawer;
    }

    private _setHome() {
        this._router?.navigate("/", { silent: true });
    }

    protected override _openDialog(dialog: string, options: { home: boolean } = { home: true }) {
        this._log.debug(`openDialog: ${dialog}`);
        if (options.home) {
            this._setHome();
        }

        super._openDialog(dialog);
    }

    ///----------------------------------------------------------------------
    // Places
    ///----------------------------------------------------------------------

    private _setSelectedPlace(place: IPlace | undefined) {
        this._selectedPlace = place;
    }

    private _clearSelectedPlace() {
        this._selectedPlace = undefined;
    }

    private async _selectPlace(id: string) {
        if (this._importSelectingPlace) {
            (this.shadowRoot?.getElementById("media-import") as MediaImport)?.placedSelected(id);
            this._importSelectingPlace = false;
            this._getImportDrawer()?.show();
        } else {
            const place = await this._placesTable.read(id);
            if (place) {
                this._mapGoTo(place.data.location, undefined);
                this._setSelectedPlace(cloneDeep(place));
                this._openDialog("place-view", { home: false });
            }
        }
    }

    private async _addNewPlace(location: ILocation | undefined = undefined) {
        const addPlace = async (location: ILocation) => {
            const place = makeEmptyPlace();
            place.data.location = locationToFixed(location);
            place.data.locationKnown = true;
            place.softDeleted = "false";
            this._setSelectedPlace(place);
            this._openDialog("place-view", { home: false });
        };
        if (location) {
            await addPlace(location);
        } else {
            navigator.geolocation.getCurrentPosition(
                async (pos: GeolocationPosition) => {
                    await addPlace({
                        lat: pos.coords.latitude,
                        lon: pos.coords.longitude,
                    });
                },
                () => {},
                {
                    enableHighAccuracy: true,
                }
            );
        }
    }

    ///----------------------------------------------------------------------
    // Place moving
    ///----------------------------------------------------------------------

    private _onMovePlaceStart(placeId: string | undefined) {
        if (placeId) {
            this._movingPlace = true;
            this._getPlaceViewer()?.hide();
            (this.shadowRoot?.getElementById("map") as PlacesMap)?.startPlaceMove(placeId);
        }
    }

    private _onMovePlaceEnd(placeId: string) {
        if (this._selectedPlace?.id === placeId) {
            this._getPlaceViewer()?.show();
        }
        this._movingPlace = false;
    }

    private _onMovePlace(placeId: string, location: ILocation) {
        if (this._selectedPlace?.id === placeId) {
            this._getPlaceViewer()?.setPlaceLocation(location);
        }
    }

    private _onMovePlaceCancel() {
        this._movingPlace = false;
        this._getPlaceViewer()?.show();
    }

    ///----------------------------------------------------------------------
    // Navigation
    ///----------------------------------------------------------------------

    private _onNavigationStarted() {
        this._closeDrawer();
        this._closeDialog();
        this._navigating = true;
    }

    private _onNavigationStopped() {
        this._navigating = false;
    }

    protected override _onDialogAfterHide() {
        this._currentSelectedLocation = undefined;
    }

    private _drawerAfterHide(e: Event) {
        if (e.eventPhase === e.AT_TARGET) {
            this._currentDrawer = undefined;
            this._setHome();
            return true;
        }
        return false;
    }

    private _renderDeleteConfirmDialog() {
        return html`<ds-confirm-dialog
            open
            prompt="Delete place?"
            message="This will irretrievably delete the place. Press 'Yes' to delete or 'Cancel' to abort."
            @on-approved="${async () => {
                if (this._currentDeletePlaceId) {
                    await this._placesTable.delete(this._currentDeletePlaceId);
                    this._currentDeletePlaceId = undefined;
                }
            }}"
            @on-cancel=${() => {
                this._currentDeletePlaceId = undefined;
                this._currentDialog = undefined;
            }}
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        ></ds-confirm-dialog>`;
    }

    private _renderNewVersionDialog() {
        return html`<sl-dialog
            open
            label="New version is available, please reload!"
            id="new-release-dialog"
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            <sl-button @click=${() => forceReloadPage()}>Reload page</sl-button>
            <div style="max-height:calc(100vh * 0.6);overflow-y:scroll">
                <ds-markdown url="./release-notes.md" cache="no-cache"></ds-markdown>
            </div>
        </sl-dialog>`;
    }

    private _renderReleaseNotesDialog() {
        return html`<sl-dialog
            open
            label="Release notes"
            id="release-notes-dialog"
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            <div style="max-height:calc(100vh * 0.6);overflow-y:scroll">
                <ds-markdown url="./release-notes.md" cache="no-cache"></ds-markdown>
            </div>
        </sl-dialog>`;
    }

    private _renderMediaGalleryDialog() {
        const renderCarousel = async () => {
            if (this._mediaGalleryMedia) {
                return html`<ds-media-carousel
                    .filter=${this._mediaGalleryFilter}
                    .mediaList=${this._mediaGalleryMedia!.map((x: IPlaceMediaItem) => {
                        return {
                            media: x.media,
                            editable: userCanEditPlaceMedia(this._kernel.user, x),
                        };
                    })}
                    .initialIndex=${this._mediaGalleryIndex}
                ></ds-media-carousel>`;
            }
            return html``;
        };

        return html`<ds-fullscreen-window
            hideCloseButton
            @ds-close=${() => {
                this._mediaGalleryOpen = false;
            }}
        >
            ${until(
                renderCarousel(),
                html`<div class="flex-row gallery-loading"><sl-spinner></sl-spinner></div>`
            )}
        </ds-fullscreen-window>`;
    }

    private _renderSettingsDrawer() {
        return html` <sl-drawer
            label="Cave DB"
            class="drawer-settings"
            placement="start"
            @sl-after-hide=${(e: Event) => this._drawerAfterHide(e)}
            open
        >
            <img slot="label" src="/assets/sidebar-logo.svg" />
            <div class="settings-drawer-container">
                <ds-settings-control
                    .view=${this._view}
                    @ds-add-new-place=${() => {
                        this._closeDrawer();
                        this._openDialog("add-place");
                    }}
                    @ds-change-view=${(e: CustomEvent) => {
                        this._view = e.detail.view;
                    }}
                    @ds-import-media=${() => {
                        this._setHome();
                        this._closeDrawer();
                        this._importingMedia = true;
                        this._openImportDrawer();
                    }}
                    @ds-show-release-notes=${() => this._openDialog("release-notes")}
                    @ds-show-contributions=${() => this._openDialog("contributor-stats")}
                    @ds-show-changelog=${() => this._router?.navigate("/changelog")}
                    @ds-show-moderation-queue=${() => this._router?.navigate("/moderation")}
                    @ds-show-media-uploads=${() => this._router?.navigate("/uploads")}
                    @ds-export-gpx=${() => this._openDialog("gpx-export")}
                    @ds-offline-maps=${() => this._openDialog("offline-maps")}
                ></ds-settings-control>
            </div>
            <div slot="footer" class="settings-footer">
                <div>
                    <span>Version ${this._kernel.currentVersion}</span>
                    <sl-button size="small" @click=${() => forceReloadPage()}>reload</sl-button>
                </div>
                <sl-button variant="default" @click="${this._closeDrawer}">Close</sl-button>
            </div>
        </sl-drawer>`;
    }

    private _renderJournalDrawer() {
        return html` <sl-drawer
            label="Latest updates"
            class="drawer-journal"
            placement="end"
            @sl-after-hide=${() => this._router?.navigate("/")}
            open
        >
            <ds-journal-list></ds-journal-list>
        </sl-drawer>`;
    }

    private _renderFilterDrawer() {
        return html`
            <sl-drawer
                label="Filter"
                class="drawer-filter"
                placement="end"
                open
                @sl-after-hide=${(e: Event) => {
                    this._drawerAfterHide(e);
                }}
            >
                <ds-filter-editor @ds-close=${() => this._closeDrawer()}></ds-filter-editor>
            </sl-drawer>
        `;
    }

    private _mapGoTo(location: ILocation, zoom: number | undefined = undefined) {
        (this.shadowRoot?.getElementById("map") as PlacesMap)?.goTo(location, zoom);
    }

    private _renderImportDrawer() {
        return html` <sl-drawer
            label="Import media"
            id="drawer-import"
            placement="end"
            @sl-after-hide=${(e: Event) => {
                if (!this._importSelectingPlace) {
                    if (this._drawerAfterHide(e)) {
                        this._mapPOIS = [];
                        this._currentImportMediaTarget = undefined;
                    }
                }
            }}
            open
        >
            <ds-media-import
                no-header
                .place=${this._currentImportMediaTarget as IPlace}
                id="media-import"
                @select-place=${() => {
                    this._importSelectingPlace = true;
                    this._getImportDrawer()?.hide();
                }}
                @photo-location-changed=${(e: CustomEvent) => {
                    this._mapPOIS = [
                        {
                            location: e.detail.location,
                        },
                    ];
                }}
                @go-to-location=${(e: CustomEvent) => {
                    this._mapGoTo(e.detail.location, MAX_ZOOM);
                }}
                @ds-close=${() => {
                    this._mapPOIS = [];
                    this._currentImportMediaTarget = undefined;
                    this._closeDrawer();
                    if (this._currentImportMediaTarget) {
                        this._currentImportMediaTarget = undefined;
                        this._openDialog("place-view");
                    }
                }}
            ></ds-media-import>
        </sl-drawer>`;
    }

    private _renderModerationDrawer() {
        return html` <sl-drawer
            label="Contribution queue"
            id="drawer-moderation"
            placement="end"
            @sl-after-hide=${(e: Event) => {
                this._drawerAfterHide(e);
            }}
            open
        >
            <ds-moderation-list></ds-moderation-list>
            <sl-button slot="footer" variant="default" @click="${this._closeDrawer}"
                >Close</sl-button
            >
        </sl-drawer>`;
    }

    private _renderUploadsDrawer() {
        return html` <sl-drawer
            label="Uploads"
            id="drawer-uploads"
            placement="end"
            @sl-after-hide=${(e: Event) => {
                this._drawerAfterHide(e);
            }}
            open
        >
            <ds-media-upload-browser></ds-media-upload-browser>
            <sl-button slot="footer" variant="default" @click="${this._closeDrawer}"
                >Close</sl-button
            >
        </sl-drawer>`;
    }

    private _renderPageHeader() {
        return html`
            <div class="header-container flex-column">
                <div class="header">
                    <sl-icon-button
                        name="list"
                        label="Settings"
                        @click="${this._openSettingsDrawer}"
                    ></sl-icon-button>

                    <span class="app-title">${this.appTitle}</span>

                    <div class="header-buttons">
                        ${this._trackLocation
                            ? html` <sl-icon-button
                                  library="local"
                                  name="satellite-dish-solid"
                              ></sl-icon-button>`
                            : ``}
                        ${litIf(
                            this._signedIn,
                            () => html`
                                <sl-tooltip content="View change log">
                                    <sl-icon-button
                                        name="clock-history"
                                        label="View change log"
                                        @click="${() => this._openJournalDrawer()}"
                                    ></sl-icon-button>
                                </sl-tooltip>
                                <sl-icon-button
                                    name="person-circle"
                                    label="Profile"
                                    @click=${() => {
                                        window.location.href = `/#/user/${this._kernel.user!.id}`;
                                    }}
                                ></sl-icon-button>
                            `,
                            () => html`
                                <sl-button
                                    class="borderless sign-up"
                                    @click=${() => this._openDialog("sign-in")}
                                    >Sign up | log in</sl-button
                                >
                            `
                        )}
                    </div>
                </div>
            </div>
        `;
    }

    private _renderNavBar() {
        return html`${litIf(
            this._navigating,
            () =>
                html`<div class="nav-direction header">
                    <ds-navigator></ds-navigator>
                </div>`
        )} `;
    }

    private _setMapMarkers(type: MarkerType) {
        const button = this.shadowRoot?.getElementById("map-markers") as SlButton;
        button.innerText = `Map markers : ${type === "points" ? "Points" : "Details"}`;
    }

    private _renderBody() {
        return html`${choose(this._view, [
            [
                "map",
                () =>
                    html` <ds-places-map
                            id="map"
                            persistent
                            ?showDebug=${this._kernel.enableDebugInfo.get()}
                            ?showExitNavigationButton=${this._navigating}
                            ?showcurrentposition=${true}
                            ?trackcurrentposition=${this._trackLocation}
                            .placesOfInterest=${this._mapPOIS}
                            .mapType=${this._kernel.mapTileLayer.get()}
                            .markerType=${this._mapMarkerType}
                            @place-selected=${(e: CustomEvent) => {
                                this._router?.navigate(`/place/${e.detail.placeId}`, {
                                    silent: true,
                                });
                                this._selectPlace(e.detail.placeId);
                            }}
                            @place-move-end=${(e: CustomEvent) => {
                                this._onMovePlace(e.detail.placeId, e.detail.location);
                                this._onMovePlaceEnd(e.detail.placeId);
                            }}
                            @place-move-cancel=${() => this._onMovePlaceCancel()}
                            @map-location-selected=${(e: CustomEvent) => {
                                this._currentSelectedLocation = e.detail.location;
                                this._openDialog("add-place");
                            }}
                            @map-center-changed=${(e: CustomEvent) =>
                                (this._mapCenter = e.detail.center)}
                            @ds-map-type-changed=${(e: CustomEvent) =>
                                this._kernel.mapTileLayer.set(e.detail.type)}
                            @ds-home-selected=${() => {
                                showConfirmDialog(
                                    {
                                        open: true,
                                        prompt: "Navigate to car?",
                                        message: "Press 'Yes' to delete or 'Cancel' to abort.",
                                    },
                                    () => {
                                        this._kernel.events.raiseEvent("ds-navigate", {
                                            location: this._kernel.currentHomeLocation.get(),
                                        });
                                    }
                                );
                            }}
                        ></ds-places-map>
                        ${litIf(
                            this._showCompass && !this._navigating,
                            () => html`<ds-compass class="compass"></ds-compass>`
                        )}
                        ${litIf(
                            !this._navigating,
                            () => html`
                                <div class="search-bar">
                                    <ds-search-bar
                                        class="header"
                                        @ds-go-to-location=${(e: CustomEvent) => {
                                            // disable location tracking otherwise we will immediately
                                            // jump away from the location
                                            this._kernel.trackLocation(false);
                                            this._mapGoTo(e.detail.location, e.detail.zoom);
                                        }}
                                        @ds-add-place=${() => this._openDialog("add-place")}
                                    ></ds-search-bar>
                                    ${litIf(
                                        this._kernel.featureHeaderFilterBar.get(),
                                        () =>
                                            html`<ds-filter-bar class="filter-bar"></ds-filter-bar>`
                                    )}

                                    <div class="filter-bar-filter-editor flex-column">
                                        ${this._renderFilterBar()}
                                    </div>

                                    <div class="flex-row map-controls">
                                        <sl-select
                                            class="overlay-map-marker-select"
                                            size="medium"
                                            pill
                                            .value=${this._mapMarkerType}
                                            @sl-change=${(e: any) =>
                                                this._setMarkerType(e.target.value as MarkerType)}
                                        >
                                            <sl-option value="points">Show points</sl-option>
                                            <sl-option value="details">Show details</sl-option>
                                        </sl-select>
                                    </div>

                                    <ds-tool-drawer
                                        class="overlay-filter-editor ds-border"
                                        .tool=${this._lastToolDrawerTool.get()}
                                        @ds-tool-changed=${(e: CustomEvent) =>
                                            this._lastToolDrawerTool.set(e.detail.tool)}
                                        @ds-tool-opened=${(_e: CustomEvent) => {
                                            this.shadowRoot
                                                ?.querySelector("ds-tool-drawer")
                                                ?.classList.add("open");
                                        }}
                                        @ds-tool-closed=${(_e: CustomEvent) => {
                                            this.shadowRoot
                                                ?.querySelector("ds-tool-drawer")
                                                ?.classList.remove("open");
                                        }}
                                    ></ds-tool-drawer>
                                </div>
                            `,
                            () => html`<ds-navigator></ds-navigator>`
                        )}
                        ${litIf(
                            this._syncing,
                            () =>
                                html`<div class="sync-message">
                                    <div class="flex-column">
                                        <span>Synchronizing data</span
                                        ><sl-progress-bar indeterminate></sl-progress-bar>
                                    </div>
                                </div>`
                        )}`,
            ],
            [
                "data-table",
                () =>
                    html`<ds-data-table
                        @ds-delete-place=${(event: CustomEvent) => {
                            this._currentDeletePlaceId = event.detail.placeId;
                            this._clearSelectedPlace();
                            this._closeDialog();
                            this._openDialog("delete-confirm");
                        }}
                    ></ds-data-table>`,
            ],
        ])}`;
    }

    private _renderFilterBar() {
        return html`<div class="filter-footer-row">
            <div class="flex-row-centered">
                <ds-data-table-select table="userFilters"> </ds-data-table-select>
                ${litIf(
                    this._showResetFilterButton,
                    () =>
                        html` <sl-button
                            pill
                            style="margin-left:auto;"
                            size="small"
                            @click=${() => this._kernel.currentPlaceFilter.reset()}
                            >Reset</sl-button
                        >`
                )}
            </div>
            <sl-button
                pill
                style="margin-left:auto;"
                size="small"
                @click=${() => this._openFilterDrawer()}
                >Advanced &gt;</sl-button
            >
        </div> `;
    }

    private _renderFooterBar() {
        return html`${litIf(
            !!this._infoMessage,
            () =>
                html`<div class="footer">
                    ${litIf(!!this._infoMessage, () => html`<span>${this._infoMessage}</span>`)}
                </div>`
        )}`;
    }

    private _renderGotoLocationDialog() {
        return html`<ds-go-to-location-dialog
            @on-accept=${(e: CustomEvent) => this._mapGoTo(e.detail.location, MAX_ZOOM)}
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        ></ds-go-to-location-dialog>`;
    }

    private _renderBulkImportDialog() {
        return html`<sl-dialog
            label="Bulk import places"
            open
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
            ><ds-bulk-import></ds-bulk-import
        ></sl-dialog>`;
    }

    private _renderSignInDialog() {
        return html`<sl-dialog
            label="Sign up / Log in"
            open
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            Sign up or sign in to contribute to the database and get updates and other features!
            <ds-auth-signin></ds-auth-signin>
        </sl-dialog>`;
    }

    private _renderGPXExportDialog() {
        return html`<sl-dialog
            open
            label="Export to GPX file"
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            <ds-gpx-export @on-complete=${() => this._closeDialog()}></ds-gpx-export>
        </sl-dialog>`;
    }

    private _renderOfflineMapsDialog() {
        return html`<sl-dialog
            open
            label="Going offline"
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            <ds-map-cache-control></ds-map-cache-control>
        </sl-dialog>`;
    }

    private _renderDeleteUserDialog() {
        return html` <ds-confirm-dialog
            open
            prompt="Delete the current account?"
            message="This will delete the current user completely and all contributed data will be anonymized. This is irrecoverable and you will need to create a new account."
            acceptText="Delete Account"
            @on-approved=${async () => {
                await this._kernel.deleteAccount();
            }}
        ></ds-confirm-dialog>`;
    }

    override render() {
        return html`<!-- -->
            <!-- Dialogs -->
            ${choose(this._currentDialog, [
                ["delete-user", () => this._renderDeleteUserDialog()],
                ["offline-maps", () => this._renderOfflineMapsDialog()],
                ["gpx-export", () => this._renderGPXExportDialog()],
                ["sign-in", () => this._renderSignInDialog()],
                [
                    "user-account",
                    () =>
                        html`<ds-profile-dialog
                            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
                        ></ds-profile-dialog>`,
                ],
                ["bulk-import", () => this._renderBulkImportDialog()],
                ["release-notes", () => this._renderReleaseNotesDialog()],
                ["delete-confirm", () => this._renderDeleteConfirmDialog()],
                ["new-version", () => this._renderNewVersionDialog()],
                ["go-to-location", () => this._renderGotoLocationDialog()],
                [
                    "add-place",
                    () =>
                        html`<ds-add-place-dialog
                            .selectedLocation=${this._currentSelectedLocation}
                            .mapCenter=${this._mapCenter}
                            @ds-accept=${(e: CustomEvent) => this._addNewPlace(e.detail.location)}
                            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
                        ></ds-add-place-dialog>`,
                ],
                [
                    "place-view",
                    () =>
                        html`<ds-place-view-dialog
                            viewMode=${this._selectedPlace && this._selectedPlace.id.length === 0
                                ? "editor"
                                : "normal"}
                            id="place-view"
                            .place=${this._selectedPlace}
                            noOpenInAppButton
                            @sl-after-hide=${(e: Event) => {
                                if (!this._movingPlace) {
                                    this._dialogAfterHide(e);
                                }
                            }}
                            @ds-delete-place=${() => {
                                if (this._selectedPlace) {
                                    this._currentDeletePlaceId = this._selectedPlace.id;
                                    this._clearSelectedPlace();
                                    this._closeDialog();
                                    this._openDialog("delete-confirm");
                                }
                            }}
                            @ds-move-place=${(e: CustomEvent) =>
                                this._onMovePlaceStart(e.detail.id)}
                            @ds-import-media=${(e: CustomEvent) => {
                                this._closeDialog();
                                this._currentImportMediaTarget = e.detail.place;
                                this._openImportDrawer();
                            }}
                        ></ds-place-view-dialog>`,
                ],
                [
                    "contributor-stats",
                    () =>
                        html`<ds-contributor-stats-dialog
                            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
                        ></ds-contributor-stats-dialog>`,
                ],
                [
                    "user-profile",
                    () =>
                        html`<ds-user-profile-dialog
                            @sl-after-hide=${(e: Event) => {
                                if (!this._movingPlace) {
                                    this._dialogAfterHide(e);
                                    this._navigateToSiteId = undefined;
                                    this._selectedUserId = undefined;
                                }
                            }}
                            .userId=${this._selectedUserId}
                        ></ds-user-profile-dialog>`,
                ],
            ])}

            <!-- Drawers -->
            ${choose(this._currentDrawer, [
                ["filter", () => this._renderFilterDrawer()],
                ["journal", () => this._renderJournalDrawer()],
                ["settings", () => this._renderSettingsDrawer()],
                ["import", () => this._renderImportDrawer()],
                ["moderation", () => this._renderModerationDrawer()],
                ["uploads", () => this._renderUploadsDrawer()],
            ])}
            ${this._mediaGalleryOpen ? this._renderMediaGalleryDialog() : html``}

            <div class="container-sl" id="main-container">
                <!-- Page toolbars -->
                ${this._renderPageHeader()}

                <div class="body">${this._renderBody()}</div>
                ${this._renderFooterBar()}
            </div> `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-cave-db-main-window": MainWindow;
    }
}
